import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import JsonView from '@uiw/react-json-view';
import { useEffect, useState } from 'react';
import { ApiCallFull, monitorApi } from '../services/monitor-api.service';

interface ApiCallDetailsDialogProps {
    apiCallId: number | null;
    open: boolean;
    onClose: () => void;
}

export const getStatusCodeColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
        return 'success';
    } else if (statusCode >= 300 && statusCode < 400) {
        return 'info';
    } else if (statusCode >= 400 && statusCode < 500) {
        return 'warning';
    } else if (statusCode >= 500) {
        return 'error';
    }
    return 'default';
};

export function ApiCallDetailsDialog({ apiCallId, open, onClose }: ApiCallDetailsDialogProps) {
    const [fullApiCall, setFullApiCall] = useState<ApiCallFull | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (open && apiCallId) {
            fetchApiCallDetails(apiCallId);
        } else if (!open) {
            // Reset state when dialog closes
            setFullApiCall(null);
            setError(null);
        }
    }, [open, apiCallId]);

    const fetchApiCallDetails = async (apiCallId: number) => {
        try {
            setLoading(true);
            setError(null);
            const response = await monitorApi.getApiCall(apiCallId);
            setFullApiCall(response.apiCall);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch API call details');
        } finally {
            setLoading(false);
        }
    };

    const formatJsonBody = (body: string | undefined) => {
        if (!body) return 'No body data';
        try {
            const parsed = JSON.parse(body);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return body; // Return as-is if not valid JSON
        }
    };

    const copyToClipboard = async (body: string | undefined) => {
        if (body) {
            try {
                await navigator.clipboard.writeText(formatJsonBody(body));
            } catch (err) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = formatJsonBody(body);
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
        }
    };

    const displayApiCall = fullApiCall;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : 'md'}
            fullWidth={!isMobile}
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    ...(isMobile
                        ? {
                              m: 0,
                              width: '100%',
                              height: '100%',
                              maxHeight: '100%',
                              borderRadius: 0,
                          }
                        : {
                              height: '85vh',
                              maxHeight: '85vh',
                          }),
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.5 : 2,
                }}
            >
                <Typography variant={isMobile ? 'h6' : 'h6'}>API Call Details - ID: {apiCallId}</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1 : 2,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                }}
            >
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            Loading API call details...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <Paper
                            sx={{ p: 2, backgroundColor: 'error.50', border: '1px solid', borderColor: 'error.200' }}
                        >
                            <Typography color="error">{error}</Typography>
                        </Paper>
                    </Box>
                )}

                {displayApiCall && !loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            overflow: 'hidden',
                            gap: isMobile ? 2 : 1.5,
                        }}
                    >
                        {/* Method and URL */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Chip
                                label={displayApiCall.statusCode}
                                color={getStatusCodeColor(displayApiCall.statusCode) as any}
                                size="small"
                            />
                            <Chip label={displayApiCall.method} variant="outlined" size="small" />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                }}
                            >
                                {displayApiCall.path}
                            </Typography>
                        </Box>

                        {/* Request Body Section */}
                        {displayApiCall.requestBody && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Request:
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(displayApiCall.requestBody)}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Paper
                                    sx={{
                                        p: isMobile ? 1 : 1.5,
                                        backgroundColor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        flex: 1,
                                        overflow: 'auto',
                                        minHeight: 0,
                                    }}
                                >
                                    <JsonView
                                        value={(() => {
                                            try {
                                                return JSON.parse(displayApiCall.requestBody || '{}');
                                            } catch {
                                                return { rawData: displayApiCall.requestBody || 'No request body' };
                                            }
                                        })()}
                                        style={{
                                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                                            lineHeight: isMobile ? 1.3 : 1.4,
                                            fontFamily: 'monospace',
                                            backgroundColor: 'transparent',
                                        }}
                                        collapsed={false}
                                        displayDataTypes={false}
                                        displayObjectSize={false}
                                        enableClipboard={false}
                                        shortenTextAfterLength={0}
                                    />
                                </Paper>
                            </Box>
                        )}

                        {/* Reply Body Section */}
                        {displayApiCall.replyBody && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 0.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Response:
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(displayApiCall.replyBody)}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Paper
                                    sx={{
                                        p: isMobile ? 1 : 1.5,
                                        backgroundColor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        flex: 1,
                                        overflow: 'auto',
                                        minHeight: 0,
                                    }}
                                >
                                    <JsonView
                                        value={(() => {
                                            try {
                                                return JSON.parse(displayApiCall.replyBody || '{}');
                                            } catch {
                                                return { rawData: displayApiCall.replyBody || 'No response body' };
                                            }
                                        })()}
                                        style={{
                                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                                            lineHeight: isMobile ? 1.3 : 1.4,
                                            fontFamily: 'monospace',
                                            backgroundColor: 'transparent',
                                        }}
                                        collapsed={false}
                                        displayDataTypes={false}
                                        displayObjectSize={false}
                                        enableClipboard={false}
                                        shortenTextAfterLength={0}
                                    />
                                </Paper>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 1 }}>
                <Button onClick={onClose} fullWidth={isMobile}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
